/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Search, Layers } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui'
import { CampaignCreatorTabs } from './CampaignCreatorTabs'
import { AnalysisModeCards } from './AnalysisModeCards'
import { OutputOptions } from './OutputOptions'
import { CreditPreview } from './CreditPreview'
import { CampaignCreatorForm } from './CampaignCreatorForm'
import { CompetitorList } from './CompetitorList'
import { useResearchCompetitors, useStartAudit, useStartBatchAudit, useCeleryStatus } from '@/hooks/useAmazonMutations'
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext'
import { useCampaignCreator } from './useCampaignCreator'
import type { CompetitorResult } from '@/lib/api/amazon-listing'
import type { AnalysisMode } from './AnalysisModeCards'

interface CampaignCreatorModalProps {
  open: boolean
  onClose: () => void
  initialMode?: AnalysisMode
}

export function CampaignCreatorModal({ open, onClose, initialMode = 'standard' }: CampaignCreatorModalProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useSupabaseAuth()
  const {
    tab, setTab, tld, setTld, mode, setMode, output, setOutput,
    inputValue, setInputValue, batchMode, setBatchMode, asins, setAsins,
  } = useCampaignCreator(initialMode)
  const [step, setStep] = useState<1 | 2>(1)
  const [competitors, setCompetitors] = useState<CompetitorResult[]>([])
  const [sourceAsin, setSourceAsin] = useState('')
  const research = useResearchCompetitors()
  const startAuditMutation = useStartAudit()
  const batchMutation = useStartBatchAudit()
  const { data: celeryData } = useCeleryStatus()
  const celeryAvailable = celeryData?.available ?? false

   
  useEffect(() => {
    if (!open) {
      setStep(1)
      setCompetitors([])
      setSourceAsin('')
      setBatchMode(false)
      setAsins([])
    }
  }, [open, setBatchMode, setAsins])

  if (!open) return null

  const navigateToAudit = (data: { job_id: string; audit_product_id?: string }) => {
    const base = `/v2/tools/product-listing-audit/${data.audit_product_id ?? data.job_id}`
    navigate(data.audit_product_id ? `${base}?tab=audit&audit_id=${data.job_id}` : base)
  }

  const handleStart = () => {
    if (tab === 'asin' && batchMode && asins.length > 0) {
      batchMutation.mutate(
        { asins, tld, report_language: i18n.language, mode },
        { onSuccess: (data) => {
          handleClose()
          if (data.created.length === 1) {
            const first = data.created[0]
            navigateToAudit({ job_id: first.job_id, audit_product_id: first.audit_product_id })
          } else {
            navigate('/v2/tools/product-listing-audit')
          }
        } },
      )
      return
    }
    if (mode === 'custom') {
      const asin = tab === 'asin' ? inputValue.trim().toUpperCase() : ''
      setSourceAsin(asin)
      setStep(2)
      research.mutate(
        { keyword: tab === 'keyword' ? inputValue : '', tld, source_asin: asin || undefined },
        { onSuccess: (data) => setCompetitors(data) },
      )
    } else if (tab === 'asin' && inputValue.trim()) {
      startAuditMutation.mutate(
        { asin: inputValue.trim().toUpperCase(), tld, report_language: i18n.language, mode },
        { onSuccess: (data) => { handleClose(); navigateToAudit(data) } },
      )
    }
  }

  const handleClose = () => {
    setStep(1)
    setCompetitors([])
    onClose()
  }

  const showStartButton = tab === 'asin' || tab === 'keyword'

  return (
    <div
      className="campaign-creator-modal fixed inset-0 z-50 flex items-center justify-center px-4 py-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="campaign-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div
        className="relative w-full max-w-3xl flex flex-col bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-2xl overflow-hidden"
        style={{ maxHeight: 'calc(100vh - 40px)' }}
      >
        {/* Header */}
        <div className="campaign-creator-modal-header flex items-center justify-between px-7 py-5 border-b border-neutral-100 dark:border-neutral-800 shrink-0 bg-gradient-to-b from-primary-500/5 to-transparent">
          <div className="flex items-center gap-2.5" id="campaign-modal-title">
            <div
              className={`h-7 w-7 rounded-lg flex items-center justify-center ${step === 2 ? 'bg-primary-500' : 'bg-primary-500'}`}
            >
              {step === 2
                ? <Layers className="h-3.5 w-3.5 text-white" aria-hidden="true" />
                : <Search className="h-3.5 w-3.5 text-white" aria-hidden="true" />
              }
            </div>
            <span className="text-base font-bold">
              {step === 2 ? t('campaignCreator.customModal.title') : t('campaignCreator.title')}
            </span>
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                className="text-xs text-neutral-400 hover:text-neutral-600 underline underline-offset-2 ml-1"
              >
                ← {t('campaignCreator.backToForm')}
              </button>
            )}
          </div>
          <button
            onClick={handleClose}
            aria-label={t('common.close')}
            className="h-7 w-7 rounded-lg border border-neutral-200 dark:border-neutral-700 flex items-center justify-center text-neutral-400 hover:text-red-500 hover:border-red-300 transition-colors"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>

        {/* Step 1: Campaign form */}
        {step === 1 && (
          <div className="campaign-creator-modal-step1 flex-1 overflow-y-auto flex flex-col">
            <div className="px-7 pt-5 pb-2 border-b border-neutral-100 dark:border-neutral-800 shrink-0">
              <CampaignCreatorTabs value={tab} onChange={(v) => { setTab(v); setInputValue('') }} />
            </div>
            <div className="px-7 py-6 flex flex-col gap-5 flex-1">
              <CampaignCreatorForm
                tab={tab}
                inputValue={inputValue}
                setInputValue={setInputValue}
                tld={tld}
                setTld={setTld}
                batchMode={batchMode}
                setBatchMode={setBatchMode}
                asins={asins}
                setAsins={setAsins}
                celeryAvailable={celeryAvailable}
                analysisMode={mode}
              />
              <div>
                <p className="text-2xs font-bold uppercase tracking-wider text-neutral-400 mb-2.5">
                  {t('campaignCreator.modeLabel')}
                </p>
                <AnalysisModeCards value={mode} onChange={setMode} isAdmin={user?.is_admin} />
              </div>
              <div>
                <p className="text-2xs font-bold uppercase tracking-wider text-neutral-400 mb-2.5">
                  {t('campaignCreator.outputLabel')}
                </p>
                <OutputOptions value={output} onChange={setOutput} />
              </div>
              <CreditPreview mode={mode} output={output} />
            </div>
            {showStartButton && (
              <div className="px-7 pb-6 shrink-0">
                <Button
                  variant="primary"
                  className="w-full h-[50px] text-sm font-bold"
                  onClick={handleStart}
                  disabled={startAuditMutation.isPending || batchMutation.isPending || (batchMode ? asins.length === 0 : !inputValue.trim())}
                  iconLeft={<Search className="h-4 w-4" aria-hidden="true" />}
                >
                  {(startAuditMutation.isPending || batchMutation.isPending)
                    ? t('common.loading')
                    : mode === 'custom'
                      ? t('campaignCreator.startCustom')
                      : t('campaignCreator.start')}
                </Button>
                {(startAuditMutation.isError || batchMutation.isError) && (
                  <p className="mt-2 text-xs text-red-500 text-center">
                    {t('campaignCreator.startError')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Competitor research (Custom mode) */}
        {step === 2 && (
          <div className="campaign-creator-modal-step2 flex-1 overflow-y-auto flex flex-col">
            <div className="flex-1 overflow-y-auto px-7 py-6">
              {research.isPending ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div
                    className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"
                    aria-label="Loading"
                  />
                  <p className="text-sm text-neutral-500">{t('campaignCreator.customModal.loading')}</p>
                </div>
              ) : research.isError ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <p className="text-sm text-red-500">{t('campaignCreator.customModal.error')}</p>
                  <Button variant="outline" onClick={() => setStep(1)}>
                    {t('campaignCreator.backToForm')}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                      {t('campaignCreator.customModal.resultsLabel')}
                    </span>
                    <span className="text-2xs font-bold bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-800 rounded-full px-2 py-0.5">
                      {t('campaignCreator.customModal.count', { count: competitors.length })}
                    </span>
                  </div>
                  <CompetitorList items={competitors} onChange={setCompetitors} />
                </div>
              )}
            </div>
            {!research.isPending && !research.isError && (
              <div className="px-7 py-4 border-t border-neutral-100 dark:border-neutral-800 shrink-0 bg-neutral-50 dark:bg-neutral-900/50 flex items-center justify-between">
                <span className="text-xs text-neutral-400">
                  {t('campaignCreator.customModal.footerHint', { count: competitors.length })}
                </span>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!sourceAsin) return
                    startAuditMutation.mutate(
                      { asin: sourceAsin, tld, report_language: i18n.language, mode },
                      { onSuccess: (data) => { handleClose(); navigateToAudit(data) } },
                    )
                  }}
                  disabled={competitors.length === 0 || startAuditMutation.isPending}
                  iconLeft={<Search className="h-4 w-4" aria-hidden="true" />}
                >
                  {startAuditMutation.isPending ? t('common.loading') : t('campaignCreator.customModal.cta')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
